// UnitCards.js
// "Tables" in the review dialog's file view (Spec 2 section 6.4, item 5): one
// card per unit of the file, in the file's order, each read from the unit's
// document and its entry on the file's ledger.
//
// An ingest unit's card shows its status, where it comes from ("Premiums ·
// Table 1", "Summary sheet", "pages 1–4"), its rows, how many of the table's
// columns it fills and its warnings; then what stands between it and approval
// (the ledger entry's gate), or who approved it, or that it is ready; and Review
// or Open, and Approve or Un-approve. A unit used as header values, or ignored,
// shows View (its fields) and "Ingest it" or "Use it".
//
// The section header's "⋯" menu asks for a unit step to be redone, or started
// over, on every table of the file at once.
//
// Nothing here writes: approvals go through `onUnitApproval`, file-level changes
// through `onFileChange`, both run by the dialog.

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { activeValue } from '../../../utils/decisions';
import {
  BUSY_UNIT_EDIT_BLOCK,
  statusFrom,
  unitChainProcessIds,
  unitEditBlock,
} from '../../../utils/ingestionStatus';
import { getMappingTargetColumns } from '../../../utils/mappingUtils';
import { approveUnit, requestFile, setDisposition, unapproveUnit } from '../../../services/cleaningGestures';
import { countNoun } from '../../../utils/pluralise';
import { formatText } from '../../common/TextFormatterProvider';
import { approvalLine, UnitStatusIcon, unitStatusColour, unitStatusLabel } from './ingestionStatusDisplay';

function TablesMenu({ steps, onRequest }) {
  const [anchor, setAnchor] = useState(null);
  const items = steps.flatMap((step) => [
    { key: `${step.processId}:reapply`, label: `${step.name}: redo this step on every table`, hint: 'Decode looks again, keeping your changes', step, kind: 'reapply' },
    { key: `${step.processId}:start_over`, label: `${step.name}: start this step over on every table`, hint: 'Decode decides from scratch; your changes here are forgotten', step, kind: 'start_over' },
  ]);
  return (
    <>
      <IconButton size="small" aria-label="Steps on every table" onClick={(event) => setAnchor(event.currentTarget)}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {items.map((item) => (
          <MenuItem
            key={item.key}
            dense
            onClick={() => {
              setAnchor(null);
              onRequest(item.step, item.kind);
            }}
          >
            <ListItemText primary={item.label} secondary={item.hint} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function NotIngestedCard({ unit, editBlock, deletion, onViewFields, onUse }) {
  const isContext = unit.disposition === 'context';
  return (
    <Paper variant="outlined" role="group" aria-label={unit.name} sx={{ p: 1.5, opacity: 0.85 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UnitStatusIcon status="Ignored" disposition={unit.disposition} />
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap title={unit.name}>{unit.name}</Typography>
        <Chip size="small" variant="outlined" label={isContext ? 'Header values' : 'Ignored'} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {isContext ? `${countNoun(unit.columns.length, 'value')} used by the other sheets · not ingested` : 'Not ingested'}
      </Typography>
      {deletion && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{deletion}</Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Button size="small" disabled={Boolean(deletion)} onClick={() => onViewFields(unit.id)}>View</Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={editBlock}>
          <span>
            <Button size="small" variant="outlined" disabled={Boolean(editBlock)} onClick={() => onUse(unit)}>
              {isContext ? 'Ingest it' : 'Use it'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
}

function IngestCard({
  companyId, tableId, unit, entry, file, table, batch, currentUid, namedByTheCheck, deletion, onOpenUnit, onUnitApproval,
}) {
  // The ledger entry is what the files list and the gestures read, the gate its
  // last run left included. It arrives with the file document, by another
  // listener than the unit's, so for a moment a unit may have none: its status is
  // then derived from the unit itself, and it is shown as updating, with nothing
  // to approve, until its entry arrives.
  const status = entry ? entry.status : statusFrom(unit, file, table);
  const gate = entry ? entry.gate : [];
  const updating = !entry || status === 'Cleaning' || entry.pending;
  const targets = getMappingTargetColumns(table);
  const mapRecord = unit.processes.map;
  // A target is filled when its active Map value names a source; no value, or no
  // decision at all, leaves it unmapped.
  const mapped = mapRecord
    ? targets.filter((target) => {
      const source = activeValue(mapRecord, target);
      return source !== null && source !== undefined;
    }).length
    : 0;

  const editBlock = entry ? unitEditBlock(unit.id, unit, file, batch, table) : BUSY_UNIT_EDIT_BLOCK;
  const approvable = !unit.approved && status === 'PreviewReady' && gate.length === 0 && !updating && !editBlock;
  // Un-approving is itself a change to an approved unit, so it is judged as if the
  // unit were not approved: everything else that blocks an edit still blocks it.
  let unapproveBlock = '';
  if (unit.approved) {
    unapproveBlock = entry
      ? unitEditBlock(unit.id, { ...unit, approved: null }, file, batch, table)
      : BUSY_UNIT_EDIT_BLOCK;
  }
  const inBatch = Boolean(file.batch_id);

  // Where the unit's rows come from, in the person's words.
  let location;
  if (unit.source.region_id) {
    const plan = activeValue(file.processes.segment, unit.source.sheet);
    const position = plan ? plan.regions.findIndex((region) => region.region_id === unit.source.region_id) : -1;
    location = position >= 0 ? `${unit.source.sheet} · Table ${position + 1}` : unit.source.sheet;
  } else if (unit.source.fields) {
    location = `${unit.source.sheet} sheet`;
  } else {
    location = `pages ${unit.source.pages[0]}–${unit.source.pages[1]}`;
  }

  let standing;
  if (updating) {
    standing = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">updating…</Typography>
      </Box>
    );
  } else if (gate.length > 0) {
    standing = (
      <Box>
        <Typography variant="caption" color="warning.main">Before approving:</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {gate.map((blocker) => (
            <Typography component="li" variant="caption" color="warning.main" key={blocker}>{blocker}</Typography>
          ))}
        </Box>
      </Box>
    );
  } else if (unit.approved) {
    standing = <Typography variant="caption" color="text.secondary">{approvalLine(unit.approved, currentUid)}</Typography>;
  } else {
    standing = <Typography variant="caption" color="success.main">Ready to approve</Typography>;
  }

  return (
    <Paper
      variant="outlined"
      role="group"
      aria-label={unit.name}
      sx={{ p: 1.5, borderColor: namedByTheCheck ? 'error.main' : 'divider' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UnitStatusIcon status={status} disposition={unit.disposition} />
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap title={unit.name}>{unit.name}</Typography>
        <Chip size="small" color={unitStatusColour(status)} label={unitStatusLabel(status, unit.disposition)} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {location} · {countNoun(unit.row_count, 'row')} · {mapped} of {targets.length} columns
        {unit.warnings > 0 ? ` · ${countNoun(unit.warnings, 'warning')}` : ''}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{standing}</Box>
      {namedByTheCheck && (
        <Typography variant="caption" color="error" sx={{ display: 'block' }}>Named in the problem above</Typography>
      )}
      {deletion && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{deletion}</Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Button size="small" variant="outlined" disabled={Boolean(deletion)} onClick={() => onOpenUnit(unit.id)}>
          {unit.approved || inBatch ? 'Open' : 'Review'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {unit.approved ? (
          <Tooltip title={unapproveBlock || 'Un-approve to make changes'}>
            <span>
              <Button
                size="small"
                disabled={Boolean(unapproveBlock)}
                onClick={() => onUnitApproval(
                  () => unapproveUnit(companyId, tableId, file.id, unit.id),
                  { unitId: unit.id, kind: 'unapprove', successMessage: `${unit.name} un-approved` },
                )}
              >
                Un-approve
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={editBlock || gate.join('; ')}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!approvable}
                onClick={() => onUnitApproval(
                  () => approveUnit(companyId, tableId, file.id, unit.id),
                  { unitId: unit.id, kind: 'approve', successMessage: `${unit.name} approved` },
                )}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}

function UnitCards({
  companyId,
  tableId,
  file,
  units,
  table,
  batch,
  currentUid,
  fileEditBlock: editBlock,
  onOpenUnit,
  onViewFields,
  onFileChange,
  onUnitApproval,
}) {
  const walkVerdicts = file.verdicts.filter((verdict) => verdict.process === 'walk');
  // The file's deletion state, shown on every card over the unit's own status, which
  // a deletion does not change, and disabling every control of every card (Spec 5
  // section 5): View, Review and Open here, the others through the edit blocks,
  // which put a deletion first.
  const deletion = file.deletion ? (file.deletion.failed || 'This file is being deleted.') : '';

  // The unit steps that can be asked for on every table at once, in the unit
  // chain's order: the columns, each value-changing rule, each classified column
  // and each skip rule. The other steps hold nothing a person decides.
  const rulesById = new Map(Object.values(table.ingestion_rules || {}).map((rule) => [rule.id, rule]));
  const steps = [];
  unitChainProcessIds(table).forEach((processId) => {
    if (processId === 'map') {
      steps.push({ processId, name: 'Columns' });
      return;
    }
    // 'rule:{rule id}', 'classify:{column}', 'skip:{rule id}'; a column name may hold a colon.
    const separator = processId.indexOf(':');
    if (separator === -1) return;
    const kind = processId.slice(0, separator);
    const subject = processId.slice(separator + 1);
    if (kind === 'classify') steps.push({ processId, name: formatText(subject) });
    else steps.push({ processId, name: `Rule: ${rulesById.get(subject).name}` });
  });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 2, mb: 1 }}>
        <Typography variant="h6">Tables</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          review and approve each one, or approve the file to approve them all.
        </Typography>
        {!editBlock && units.length > 0 && (
          <TablesMenu
            steps={steps}
            onRequest={(step, kind) => onFileChange(
              () => requestFile(companyId, tableId, file.id, step.processId, kind),
            )}
          />
        )}
      </Box>
      {units.length === 0 ? (
        <Typography variant="caption" color="text.secondary">None yet.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {units.map((unit) => (unit.disposition === 'ingest' ? (
            <IngestCard
              key={unit.id}
              companyId={companyId}
              tableId={tableId}
              unit={unit}
              entry={file.units[unit.id]}
              file={file}
              table={table}
              batch={batch}
              currentUid={currentUid}
              namedByTheCheck={walkVerdicts.some((verdict) => (verdict.units || []).includes(unit.id))}
              deletion={deletion}
              onOpenUnit={onOpenUnit}
              onUnitApproval={onUnitApproval}
            />
          ) : (
            <NotIngestedCard
              key={unit.id}
              unit={unit}
              editBlock={editBlock}
              deletion={deletion}
              onViewFields={onViewFields}
              onUse={() => onFileChange(() => setDisposition(companyId, tableId, file.id, unit.id, 'ingest'))}
            />
          )))}
        </Box>
      )}
    </>
  );
}

export default UnitCards;
